/**
 * CommissionSplitEditor — reusable multi-row editor for the adviser
 * commission split on a file. Used on the submission form and on the
 * file detail view (while the file is still editable).
 *
 * Each row = adviser/entity name (text) + percentage (integer 0..100).
 * Minimum one row enforced. Names typed manually for now (no managed
 * adviser list). Decimals not supported.
 *
 * The component is fully controlled — `value` is an array of
 * `{ name, percent }` entries; every edit calls `onChange` with the
 * next array and `onValidityChange` (when supplied) with the latest
 * `isValid` flag. `isValid` is true only when (a) every row has a
 * non-empty trimmed name and (b) the percentages sum to exactly 100.
 *
 * Visual posture: neutral surfaces, no brand colours; subtle bordered
 * panel grouping the rows. Below the rows: "+ Add adviser" on the
 * left, total indicator pill on the right.
 */

import React, { useEffect, useMemo, useRef } from 'react';
import { Plus, X } from 'lucide-react';

const DEFAULT_SPLITS = [
  { name: 'M Lourens', percent: 70 },
  { name: 'Optimum PFP', percent: 30 },
];

/**
 * Compute whether the supplied splits are valid for submission.
 * - Total of percents must equal exactly 100.
 * - Every row must have a non-empty trimmed name.
 */
export function isCommissionSplitsValid(splits) {
  if (!Array.isArray(splits) || splits.length === 0) return false;
  let total = 0;
  for (const row of splits) {
    if (!row || typeof row.name !== 'string' || row.name.trim() === '') {
      return false;
    }
    const p = Number(row.percent);
    if (!Number.isInteger(p) || p < 0 || p > 100) return false;
    total += p;
  }
  return total === 100;
}

/**
 * Default split list — exported so callers (e.g. the submission form)
 * can seed the editor without duplicating the constant.
 */
export function defaultCommissionSplits() {
  return DEFAULT_SPLITS.map((s) => ({ ...s }));
}

export default function CommissionSplitEditor({
  value,
  onChange,
  onValidityChange,
  disabled = false,
}) {
  const splits = Array.isArray(value) && value.length > 0 ? value : defaultCommissionSplits();

  // Compute total + validity flags. `total` is summed permissively
  // (treating non-numbers as 0) so the indicator pill always reflects
  // what the user has typed; `isValid` is the strict gate used by
  // parents to enable submit.
  const { total, hasEmptyName, isValid } = useMemo(() => {
    let t = 0;
    let empty = false;
    for (const row of splits) {
      const p = Number(row?.percent);
      t += Number.isFinite(p) ? p : 0;
      if (!row || typeof row.name !== 'string' || row.name.trim() === '') {
        empty = true;
      }
    }
    return {
      total: t,
      hasEmptyName: empty,
      isValid: isCommissionSplitsValid(splits),
    };
  }, [splits]);

  // Push validity changes upstream. Use a ref so we only fire when the
  // boolean flips — parents typically wire this into form-level state.
  const lastValidityRef = useRef(null);
  useEffect(() => {
    if (!onValidityChange) return;
    if (lastValidityRef.current === isValid) return;
    lastValidityRef.current = isValid;
    onValidityChange(isValid);
  }, [isValid, onValidityChange]);

  const updateRow = (index, patch) => {
    const next = splits.map((row, i) => (i === index ? { ...row, ...patch } : row));
    onChange(next);
  };

  const addRow = () => {
    // New rows start with empty name + empty percent (string '' rather
    // than 0) so the user has to balance manually. We still store the
    // underlying value as null until the user types something — the
    // strict validity check then flags the row as invalid.
    onChange([...splits, { name: '', percent: '' }]);
  };

  const removeRow = (index) => {
    if (splits.length <= 1) return;
    onChange(splits.filter((_, i) => i !== index));
  };

  // Indicator pill text + colour scheme. Three states: green (valid),
  // amber (numeric over/under or empty name).
  let pill;
  if (hasEmptyName) {
    pill = { label: 'Adviser name missing', tone: 'amber' };
  } else if (total === 100) {
    pill = { label: '100% ✓', tone: 'green' };
  } else if (total < 100) {
    pill = { label: `${total}% — ${100 - total}% short`, tone: 'amber' };
  } else {
    pill = { label: `${total}% — ${total - 100}% over`, tone: 'amber' };
  }

  const pillStyle = pill.tone === 'green' ? styles.pillGreen : styles.pillAmber;

  return (
    <div style={styles.panel}>
      <div style={styles.rows}>
        {splits.map((row, idx) => {
          const isOnly = splits.length === 1;
          const percentDisplay =
            row.percent === '' || row.percent == null ? '' : String(row.percent);
          return (
            <div key={idx} style={styles.row}>
              <input
                type="text"
                value={row.name || ''}
                onChange={(e) => updateRow(idx, { name: e.target.value })}
                placeholder="Adviser or entity name"
                style={styles.nameInput}
                disabled={disabled}
                maxLength={120}
              />
              <div style={styles.percentWrap}>
                <input
                  type="number"
                  min={0}
                  max={100}
                  step={1}
                  value={percentDisplay}
                  onChange={(e) => {
                    const raw = e.target.value;
                    if (raw === '') {
                      updateRow(idx, { percent: '' });
                      return;
                    }
                    // Coerce to a clamped integer. parseInt strips any
                    // accidental decimals — decimals are not supported.
                    const parsed = parseInt(raw, 10);
                    if (Number.isNaN(parsed)) return;
                    const clamped = Math.max(0, Math.min(100, parsed));
                    updateRow(idx, { percent: clamped });
                  }}
                  placeholder="0"
                  style={styles.percentInput}
                  disabled={disabled}
                  aria-label="Percentage"
                />
                <span style={styles.percentSign}>%</span>
              </div>
              <button
                type="button"
                onClick={() => removeRow(idx)}
                style={{
                  ...styles.removeBtn,
                  ...(isOnly ? styles.removeBtnDisabled : {}),
                }}
                disabled={disabled || isOnly}
                aria-label={`Remove ${row.name || 'row ' + (idx + 1)}`}
                title={isOnly ? 'At least one adviser is required' : 'Remove this adviser'}
              >
                <X size={14} />
              </button>
            </div>
          );
        })}
      </div>

      <div style={styles.divider} />

      <div style={styles.footer}>
        <button
          type="button"
          onClick={addRow}
          style={styles.addBtn}
          disabled={disabled}
        >
          <Plus size={14} /> Add adviser
        </button>
        <span style={pillStyle}>{pill.label}</span>
      </div>
    </div>
  );
}

const styles = {
  panel: {
    background: '#FFFFFF',
    border: '1.5px solid #D0DAE6',
    borderRadius: '10px',
    padding: '0.85rem',
  },
  rows: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  },
  nameInput: {
    flex: 1,
    minWidth: 0,
  },
  percentWrap: {
    position: 'relative',
    flexShrink: 0,
    width: '5.5rem',
  },
  percentInput: {
    width: '100%',
    paddingRight: '1.6rem',
    textAlign: 'right',
    fontVariantNumeric: 'tabular-nums',
  },
  percentSign: {
    position: 'absolute',
    right: '0.65rem',
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#6B7A8D',
    fontSize: '0.85rem',
    pointerEvents: 'none',
  },
  removeBtn: {
    flexShrink: 0,
    width: '2rem',
    height: '2rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'transparent',
    border: '1px solid #D0DAE6',
    borderRadius: '8px',
    color: '#6B7A8D',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  removeBtnDisabled: {
    opacity: 0.4,
    cursor: 'not-allowed',
  },
  divider: {
    height: '1px',
    background: '#E8EFF5',
    margin: '0.85rem 0 0.65rem',
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '0.75rem',
    flexWrap: 'wrap',
  },
  addBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.35rem',
    padding: '0.4rem 0.7rem',
    background: '#F0F4F8',
    border: '1px solid #D0DAE6',
    borderRadius: '8px',
    color: '#0F4C75',
    fontSize: '0.8rem',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  pillGreen: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '0.3rem 0.7rem',
    borderRadius: '999px',
    background: 'rgba(46, 196, 182, 0.15)',
    color: '#138672',
    fontSize: '0.78rem',
    fontWeight: 600,
    fontVariantNumeric: 'tabular-nums',
  },
  pillAmber: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '0.3rem 0.7rem',
    borderRadius: '999px',
    background: 'rgba(244, 162, 97, 0.18)',
    color: '#B7651E',
    fontSize: '0.78rem',
    fontWeight: 600,
    fontVariantNumeric: 'tabular-nums',
  },
};
